/**
 * The set of v2 scalar metric keys the backend exposes. camelCase end-to-end —
 * these strings appear in querystrings AND as response object keys.
 */
export type ScalarMetricKey =
  | 'aiMessages'
  | 'aiAcceptanceRate'
  | 'aiCoverage'
  | 'aiTicketCoverage'
  | 'aiAutomation'
  | 'aiAccuracy'
  | 'aiEdits'
  | 'avgAiResponsesPerTicket'
  | 'aiSurfacedAccuracy'
  | 'aiGeneratedCoverage'
  | 'aiSurfacedCoverage';

/**
 * The metrics shown on the Message Metrics tab's Overview section. Order
 * matters (grid layout). `avgAiResponsesPerTicket` is a member of the
 * `ScalarMetricKey` union but lives on the Ticket Metrics tab; it is fetched
 * by `TicketMetricsSection`, not by `OverviewSection`.
 */
export const SCALAR_METRIC_KEYS: ScalarMetricKey[] = ['aiMessages', 'aiAcceptanceRate', 'aiCoverage', 'aiAutomation', 'aiAccuracy', 'aiEdits'];

/**
 * Scalar keys fetched on the Ticket Metrics tab. Three real metrics (the
 * `Productivity Gain` card is a placeholder, no backend call yet).
 *   - aiTicketCoverage powers "Tickets Handled by AI" (distinct conversations
 *     handled by AI / distinct CS conversations, rendered as count/total)
 *   - avgAiResponsesPerTicket powers "Avg AI responses /ticket"
 *   - aiAutomation powers "Automation Rate" (rendered as percent)
 */
export const TICKET_METRIC_KEYS: ScalarMetricKey[] = ['aiTicketCoverage', 'avgAiResponsesPerTicket', 'aiAutomation'];

/**
 * One snapshot — count, total, and the derived percent.
 */
export interface MetricSnapshot {
  count: number;
  total: number;
  percent: number | null;
}

/**
 * Two snapshots side-by-side plus both trend forms:
 *  - `countChangePct` — relative growth of the count vs prev period (300 → 360 = +20%)
 *  - `percentPointChange` — literal subtraction of two percents (30% → 36% = +6 pp)
 *
 * The card decides which trend to surface based on what it's displaying.
 */
export interface ScalarMetricResult {
  current: MetricSnapshot;
  previous: MetricSnapshot;
  trend: {
    countChangePct: number | null;
    percentPointChange: number | null;
  };
}

export type ScalarMetricsBatch = Partial<Record<ScalarMetricKey, ScalarMetricResult>>;

export interface AnalyticsV2QueryParams {
  fromDate: string; // YYYY-MM-DD
  toDate: string;   // YYYY-MM-DD
  /**
   * Super-admin override: fetch analytics for this account instead of the
   * caller's own account (derived from the Bearer claim server-side). Omitted
   * for normal users, who always see their own account.
   */
  accountId?: number;
  /**
   * Business unit (brand) to scope the response to. Absent = the whole account.
   *
   * A STRING, not a number: `business_units.id` is a BIGINT and the pg driver hands it
   * back as a string, so it stays a string end to end (see `business-unit.store.ts`).
   * Mutually exclusive with `accountId` in practice — a super admin viewing another
   * account has no brands there, so the hooks drop it when an override is set.
   */
  buId?: string | null;
}

/**
 * Series metric keys mirror scalar keys — a card and its trend chart share one
 * identifier end-to-end. Only metrics with a backend series implementation
 * appear here; cards without series simply don't render a chart.
 */
export type SeriesMetricKey = 'aiMessages' | 'aiAcceptanceRate' | 'aiCoverage' | 'aiAutomation' | 'aiAccuracy' | 'aiEdits';

export type Granularity = 'day' | 'week' | 'month';

export interface SeriesBucket {
  date: string;        // YYYY-MM-DD — start of bucket
  count: number;
  total: number;
  percent: number | null;
}

export interface SeriesMetricResult {
  granularity: Granularity;
  buckets: SeriesBucket[];
}

export type SeriesMetricsBatch = Partial<Record<SeriesMetricKey, SeriesMetricResult>>;

export interface AnalyticsV2SeriesQueryParams extends AnalyticsV2QueryParams {
  granularity: Granularity;
}


// ─── Ticket breakdown ─────────────────────────────────────────────────────────
// CS tickets grouped by one level of their classification (their first CS customer
// message), from `GET /api/analytics/categories`.

export type TicketBreakdownLevel = 'category' | 'subcategory' | 'intent';

export interface TicketBreakdownRow {
  /** The category, subcategory or intent id; null when the tickets have none. */
  key: string | null;
  tickets: number;
  aiHandled: { count: number; percent: number | null };
  automated: { count: number; percent: number | null };
  avgAiResponses: number | null;
}

/** The level shown and the category / subcategory it sits inside. */
export interface TicketBreakdownScope {
  by: TicketBreakdownLevel;
  category?: string;
  subcategory?: string;
}

export interface TicketBreakdownQueryParams extends AnalyticsV2QueryParams, TicketBreakdownScope {}


// ─── Drill-down ───────────────────────────────────────────────────────────────
// The rows behind a number, from `GET /api/analytics/drilldown`: the rows a metric counts
// (`count`) or counts them against (`total`), one page at a time.

export type DrilldownMetricKey = 'aiTicketCoverage' | 'aiAutomation' | 'avgAiResponsesPerTicket';
export type DrilldownPart = 'count' | 'total';

/** Dimension filters; null selects the rows with no value (Uncategorized). */
interface DrilldownFilters {
  category?: string | null;
  subcategory?: string | null;
  intent?: string | null;
}

/** What a drill-down lists, independent of the page shown. */
export interface DrilldownRequest {
  metric: DrilldownMetricKey;
  part: DrilldownPart;
  filters: DrilldownFilters;
}

export interface DrilldownQueryParams extends AnalyticsV2QueryParams, DrilldownRequest {
  page: number;
  pageSize: number;
}

export interface TicketDrilldownRow {
  conversationId: number;
  /** Kim's ticket number. */
  displayId: number | null;
  /** The helpdesk's ticket id. */
  externalId: string | null;
  createdAt: string | null;
  /** The start of its first CS customer message. */
  preview: string | null;
  category: string | null;
  subcategory: string | null;
  intent: string | null;
  handled: boolean;
  automated: boolean;
  aiResponses: number;
}

export interface DrilldownPage<Row> {
  /** Every row behind the number, across pages. */
  total: number;
  page: number;
  pageSize: number;
  rows: Row[];
}


// ─── Coverage Funnel ────────────────────────────────────────────────────────────
// The Coverage Funnel section on the super-admin Internal Analytics page.
// Mirrors the shadow report's Coverage Funnel sheet: seven stages descending
// from CS Tickets, each with its share of csTickets. Shape mirrors the backend
// response from `GET /api/analytics/funnel`.

/** One funnel stage: raw count and its share of csTickets (already *100, 2dp). */
export interface CoverageFunnelStage {
  count: number;
  pct: number | null;
}

export interface CoverageFunnelResult {
  csTickets: number;
  aiGenerated: CoverageFunnelStage; // AI Generated Response
  surfaced: CoverageFunnelStage; // Surfaced (confidence >= 0.5)
  nonSurfaced: CoverageFunnelStage; // Non-Surfaced
  hitlPending: CoverageFunnelStage; // HITL Pending
  agentAdded: CoverageFunnelStage; // Agent Added to Composer
  agentSent: CoverageFunnelStage; // Agent Sent to Customer
  hitl: { approved: number; rejected: number; pending: number };
}
